import { useCallback, useEffect, useId, useState } from 'react';
import { actingFor } from '../api/client';
import ConfirmDialog from '../components/ConfirmDialog';
import { useT } from '../i18n/i18n';
import { useLearner } from '../learners/LearnerContext';
import { createReview, deleteReview, fetchReviews, isoDay, type ReviewSummary } from './api';
import { addDays, parseDay, today, useDates } from './dates';
import ReviewEditor from './ReviewEditor';
import { useStamp } from './reviewStamp';
import './reviews.css';
import DateField from '../components/DateField';

type Range = { from: string; to: string };

function thisTerm(day: string): Range {
  const d = parseDay(day);
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  if (m >= 9) return { from: `${y}-09-01`, to: `${y}-12-31` };
  if (m < 4 || (m === 4 && d.getDate() < 15)) return { from: `${y}-01-01`, to: `${y}-04-14` };
  return { from: `${y}-04-15`, to: `${y}-07-31` };
}

function schoolYear(day: string): Range {
  const d = parseDay(day);
  const start = d.getMonth() >= 8 ? d.getFullYear() : d.getFullYear() - 1;
  return { from: isoDay(new Date(start, 8, 1)), to: isoDay(new Date(start + 1, 6, 31)) };
}

export default function ReviewsView() {
  const t = useT();
  const d = useDates();
  const stamp = useStamp();
  const id = useId();
  const { learnerId } = useLearner();
  const consultant = actingFor() != null;
  const now = today();
  const first = thisTerm(now);
  const [list, setList] = useState<ReviewSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [from, setFrom] = useState(first.from);
  const [to, setTo] = useState(first.to);
  const [title, setTitle] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<ReviewSummary | null>(null);

  const load = useCallback(() => {
    if (!learnerId) return;
    fetchReviews(learnerId).then((l) => { setList(l); setError(null); })
      .catch((e) => setError(e instanceof Error ? e.message : t('The reviews could not be loaded just now.')));
  }, [learnerId, t]);

  useEffect(() => { load(); }, [load]);

  if (!learnerId) return null;

  if (openId) {
    return <ReviewEditor key={openId} learnerId={learnerId} reviewId={openId} consultant={consultant} onBack={() => { setOpenId(null); load(); }} />;
  }

  const autoTitle = from && to ? t('Review {from} to {to}', { from: d.short(from), to: d.short(to) }) : '';
  const shownTitle = title ?? autoTitle;
  const pick = (r: Range) => { setFrom(r.from); setTo(r.to); setFormError(null); };

  async function start() {
    if (!learnerId) return;
    if (!from || !to) { setFormError(t('Choose both dates.')); return; }
    if (to < from) { setFormError(t('The end date must be on or after the start date.')); return; }
    if (!shownTitle.trim()) { setFormError(t('Give the review a title.')); return; }
    setBusy(true);
    setFormError(null);
    try {
      const made = await createReview(learnerId, shownTitle.trim(), from, to);
      setStarting(false);
      setTitle(null);
      setOpenId(made.id);
    } catch (e) {
      setFormError(e instanceof Error ? e.message : t('The review could not be started just now.'));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    const r = removing;
    setRemoving(null);
    if (!r || !learnerId) return;
    try {
      await deleteReview(learnerId, r.id);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('The review could not be deleted just now.'));
    }
  }

  return (
    <div className="planner-review-board">
      <p className="planner-review-intro">
        {t('A review looks back over a period — usually a term — target by target. Signing it keeps a fixed record you can share with school or the council.')}
      </p>

      {!starting && (
        <div className="toolbar">
          <button type="button" className="btn" onClick={() => setStarting(true)}>{t('Start a review')}</button>
        </div>
      )}

      {starting && (
        <section className="card planner-review-start" aria-labelledby={`${id}-start`}>
          <h3 id={`${id}-start`}>{t('Start a review')}</h3>
          <div className="toolbar planner-review-quick">
            <button type="button" className="btn secondary" onClick={() => pick(thisTerm(now))}>{t('This term')}</button>
            <button type="button" className="btn secondary" onClick={() => pick({ from: addDays(now, -83), to: now })}>{t('Last 12 weeks')}</button>
            <button type="button" className="btn secondary" onClick={() => pick(schoolYear(now))}>{t('This school year')}</button>
          </div>
          <div className="row">
            <div className="field">
              <label htmlFor={`${id}-from`}>{t('From')}</label>
              <DateField id={`${id}-from`} value={from} onChange={(v) => setFrom(v ?? '')} />
            </div>
            <div className="field">
              <label htmlFor={`${id}-to`}>{t('To')}</label>
              <DateField id={`${id}-to`} value={to} min={from} onChange={(v) => setTo(v ?? '')} />
            </div>
          </div>
          <div className="field">
            <label htmlFor={`${id}-title`}>{t('Title')}</label>
            <input id={`${id}-title`} maxLength={200} value={shownTitle} onChange={(e) => setTitle(e.target.value)} />
          </div>
          {formError && <div className="error" role="alert">{formError}</div>}
          <div className="toolbar">
            <button type="button" className="btn" disabled={busy} onClick={() => void start()}>{t('Start the review')}</button>
            <button type="button" className="btn secondary" onClick={() => { setStarting(false); setFormError(null); setTitle(null); }}>{t('Cancel')}</button>
          </div>
        </section>
      )}

      {error && <div className="error" role="alert">{error}</div>}
      {list && list.length === 0 && <p className="muted">{t('No reviews yet.')}</p>}
      {list && list.length > 0 && (
        <ul className="planner-review-list">
          {list.map((r) => (
            <li key={r.id} className="card planner-review-item">
              <div className="planner-review-item-head">
                <h3 className="planner-review-item-title">{r.title}</h3>
                <span className={`pill planner-review-status is-${r.status.toLowerCase()}`}>{r.status === 'SIGNED' ? t('Signed') : t('Draft')}</span>
              </div>
              <p className="planner-review-period">{t('From {from} to {to}', { from: d.long(r.from), to: d.long(r.to) })}</p>
              {r.signedParentName && r.signedParentAt && (
                <p className="muted">{t('Signed by {name} on {date}', { name: r.signedParentName, date: stamp(r.signedParentAt) })}</p>
              )}
              {r.signedConsultantName && r.signedConsultantAt && (
                <p className="muted">{t('Signed by {name}, consultant, on {date}', { name: r.signedConsultantName, date: stamp(r.signedConsultantAt) })}</p>
              )}
              <div className="toolbar">
                <button type="button" className="btn secondary" onClick={() => setOpenId(r.id)}>{t('Open')}</button>
                {r.status === 'DRAFT' && <button type="button" className="btn quiet-danger" onClick={() => setRemoving(r)}>{t('Delete')}</button>}
              </div>
            </li>
          ))}
        </ul>
      )}

      <ConfirmDialog
        open={removing != null}
        title={t('Delete this review?')}
        message={t('The draft and everything written in it will be removed. This cannot be undone.')}
        confirmLabel={t('Delete review')}
        destructive
        onConfirm={() => void remove()}
        onCancel={() => setRemoving(null)}
      />
    </div>
  );
}
